import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Calendar, FileText, Edit, Trash2 } from "lucide-react";
import { WorkflowStages } from "./WorkflowStages";
import { InterviewScheduler } from "./InterviewScheduler";
import { InterviewRescheduler } from "./InterviewRescheduler";
import { InterviewConductor } from "./InterviewConductor";
import { InterviewForm } from "./InterviewForm";
import { FirstInterviewForm } from "./FirstInterviewForm";
import { SecondInterviewForm } from "./SecondInterviewForm";
import { EvaluationViewer } from "./EvaluationViewer";
import { SecurityPsychAssessment } from "./SecurityPsychAssessment";
import { TestResultsTabs } from "./TestResultsTabs";
import { InterviewManager } from "./InterviewManager";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { AddGuardForm } from "@/pages/guards/components/AddGuardForm";
interface Candidate {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  status: string;
  application_date: string;
  notes?: string;
}
interface Interview {
  id: string;
  candidate_id: string;
  interview_type: string;
  scheduled_date: string;
  status: string;
  interview_data: any;
}
export function CandidateWorkflow() {
  const {
    candidateId
  } = useParams();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<string>('workflow');
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [prepopulatedGuardData, setPrepopulatedGuardData] = useState<any>(null);
  useEffect(() => {
    if (candidateId) {
      fetchCandidateData();
    }
  }, [candidateId]);
  const fetchCandidateData = async () => {
    try {
      // Fetch candidate info
      const {
        data: candidateData,
        error: candidateError
      } = await supabase.from('candidates').select('*').eq('id', candidateId).single();
      if (candidateError) throw candidateError;
      setCandidate(candidateData);

      // Fetch interviews
      const {
        data: interviewsData,
        error: interviewsError
      } = await supabase.from('interviews').select('*').eq('candidate_id', candidateId).order('scheduled_date', {
        ascending: true
      });
      if (interviewsError) throw interviewsError;
      setInterviews(interviewsData || []);
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const handleStageAction = async (action: string, data?: any) => {
    console.log('handleStageAction called with:', action, data);
    switch (action) {
      case 'schedule_first_interview':
        setCurrentView('schedule_first');
        break;
      case 'schedule_second_interview':
        setCurrentView('schedule_second');
        break;
      case 'conduct_first_interview':
        const firstInterview = interviews.find(i => i.interview_type === 'first');
        if (firstInterview) {
          setSelectedInterview(firstInterview);
          setCurrentView('conduct_first');
        }
        break;
      case 'conduct_second_interview':
        const secondInterview = interviews.find(i => i.interview_type === 'second');
        if (secondInterview) {
          setSelectedInterview(secondInterview);
          setCurrentView('conduct_second');
        }
        break;
      case 'proceed_to_second':
        await updateCandidateStatus('second_interview_scheduled');
        break;
      case 'reject_after_first':
        await updateCandidateStatus('rejected', 'Απορρίφθηκε μετά την πρώτη συνέντευξη');
        break;
      case 'start_first_test':
        setCurrentView('first_test');
        break;
      case 'start_second_test':
        setCurrentView('second_test');
        break;
      case 'start_tests': // backwards compatibility
        setCurrentView('first_test');
        break;
      case 'view_test_results':
        if (data?.testType) {
          navigate(`/hr/test-results/${candidateId}?testType=${data.testType}`);
        } else {
          navigate(`/hr/test-results/${candidateId}`);
        }
        break;
      case 'hire_candidate':
        // Block hiring if tests were skipped (second test not completed)
        try {
          const { data: secondTestRows, error: stErr } = await supabase
            .from('hr_workflow_stages')
            .select('status')
            .eq('candidate_id', candidateId)
            .eq('stage_name', 'second_test')
            .limit(1);
          if (stErr) throw stErr;
          const secondTestCompleted = Array.isArray(secondTestRows) && secondTestRows.length > 0 && secondTestRows[0].status === 'completed';
          if (!secondTestCompleted) {
            toast({ title: 'Δεν επιτρέπεται πρόσληψη', description: 'Η πρόσληψη είναι διαθέσιμη μόνο όταν ολοκληρωθούν τα τεστ.', variant: 'destructive' });
            break;
          }
        } catch (e: any) {
          toast({ title: 'Σφάλμα ελέγχου', description: e.message, variant: 'destructive' });
          break;
        }
        await handleHireCandidate();
        break;
      case 'mark_hired':
        // Same guard as hire: allow marking as hired only if tests completed
        try {
          const { data: secondTestRows, error: stErr } = await supabase
            .from('hr_workflow_stages')
            .select('status')
            .eq('candidate_id', candidateId)
            .eq('stage_name', 'second_test')
            .limit(1);
          if (stErr) throw stErr;
          const secondTestCompleted = Array.isArray(secondTestRows) && secondTestRows.length > 0 && secondTestRows[0].status === 'completed';
          if (!secondTestCompleted) {
            toast({ title: 'Δεν επιτρέπεται σήμανση πρόσληψης', description: 'Η σήμανση ως προσληφθείς απαιτεί ολοκληρωμένα τεστ.', variant: 'destructive' });
            break;
          }
        } catch (e: any) {
          toast({ title: 'Σφάλμα ελέγχου', description: e.message, variant: 'destructive' });
          break;
        }
        // Mark as hired without guard creation flow
        await updateCandidateStatus('hired');
        await supabase
          .from('hr_workflow_stages')
          .upsert({
            candidate_id: candidateId,
            stage_name: 'final_decision',
            status: 'completed',
            completed_at: new Date().toISOString(),
            notes: 'Σήμανση πρόσληψης (χωρίς δημιουργία υπαλλήλου)'
          });
        // Ensure onboarding stage is marked completed (remove from flow)
        await supabase
          .from('hr_workflow_stages')
          .upsert({
            candidate_id: candidateId,
            stage_name: 'onboarding',
            status: 'completed',
            completed_at: new Date().toISOString(),
            notes: 'Onboarding ολοκληρώθηκε με πρόσληψη'
          });
        toast({ title: 'Σημάνθηκε ως Προσληφθείς', description: 'Ο υποψήφιος επισημάνθηκε ως προσληφθείς.' });
        await fetchCandidateData();
        break;
      case 'reject_candidate':
        if (candidate?.status === 'hired') {
          toast({ title: 'Δεν επιτρέπεται απόρριψη', description: 'Ο υποψήφιος έχει ήδη προσληφθεί.', variant: 'destructive' });
          break;
        }
        await updateCandidateStatus('rejected', data?.reason || 'Τελική απόρριψη');
        // Mark final decision stage as completed with reason
        await supabase
          .from('hr_workflow_stages')
          .upsert({
            candidate_id: candidateId,
            stage_name: 'final_decision',
            status: 'completed',
            completed_at: new Date().toISOString(),
            notes: `Απόρριψη: ${data?.reason || 'Χωρίς αιτιολογία'}`
          });
        break;
      case 'skip_to_final_decision':
        // Make final decision stage available/current without tests
        await supabase
          .from('hr_workflow_stages')
          .upsert({
            candidate_id: candidateId,
            stage_name: 'final_decision',
            status: 'in_progress',
            notes: 'Παράκαμψη τεστ λόγω κωλύματος μετά τη 2η συνέντευξη'
          });
        // Update candidate status to trigger UI refresh and reflect that testing is effectively completed
        await supabase
          .from('candidates')
          .update({ status: 'testing_completed' })
          .eq('id', candidateId);
        toast({ title: 'Έτοιμος για Τελική Απόφαση', description: 'Τα ψυχομετρικά παρακάμφθηκαν για αυτόν τον υποψήφιο.' });
        await fetchCandidateData();
        break;
      case 'reschedule_first_interview':
        if (data) {
          setSelectedInterview(data);
          setCurrentView('reschedule_first');
        }
        break;
      case 'reschedule_second_interview':
        if (data) {
          setSelectedInterview(data);
          setCurrentView('reschedule_second');
        }
        break;
      case 'edit_first_interview':
        const firstInterviewEdit = interviews.find(i => i.interview_type === 'first');
        if (firstInterviewEdit) {
          setSelectedInterview(firstInterviewEdit);
          setCurrentView('conduct_first');
        }
        break;
      case 'edit_second_interview':
        const secondInterviewEdit = interviews.find(i => i.interview_type === 'second');
        if (secondInterviewEdit) {
          setSelectedInterview(secondInterviewEdit);
          setCurrentView('conduct_second');
        }
        break;
      case 'evaluate_first_interview':
        const firstInterviewToEvaluate = interviews.find(i => i.interview_type === 'first');
        if (firstInterviewToEvaluate) {
          setSelectedInterview(firstInterviewToEvaluate);
          setCurrentView('evaluate_first');
        }
        break;
      case 'evaluate_second_interview':
        const secondInterviewToEvaluate = interviews.find(i => i.interview_type === 'second');
        if (secondInterviewToEvaluate) {
          setSelectedInterview(secondInterviewToEvaluate);
          setCurrentView('evaluate_second');
        }
        break;
      case 'view_first_evaluation':
        const firstInterviewForView = interviews.find(i => i.interview_type === 'first');
        if (firstInterviewForView) {
          setSelectedInterview(firstInterviewForView);
          setCurrentView('view_first_evaluation');
        }
        break;
      case 'view_second_interview':
        if (data) {
          setSelectedInterview(data);
          setCurrentView('view_second_interview');
        }
        break;
      case 'view_second_evaluation':
        const secondInterviewForView = interviews.find(i => i.interview_type === 'second' && i.status === 'completed');
        if (secondInterviewForView) {
          setSelectedInterview(secondInterviewForView);
          setCurrentView('view_second_evaluation');
        }
        break;
      case 'edit_first_evaluation':
        const firstInterviewForEdit = interviews.find(i => i.interview_type === 'first' && i.status === 'completed');
        if (firstInterviewForEdit) {
          setSelectedInterview(firstInterviewForEdit);
          setCurrentView('edit_first_evaluation');
        }
        break;
      case 'edit_second_evaluation':
        const secondInterviewForEdit = interviews.find(i => i.interview_type === 'second' && i.status === 'completed');
        if (secondInterviewForEdit) {
          setSelectedInterview(secondInterviewForEdit);
          setCurrentView('edit_second_evaluation');
        }
        break;
      case 'view_test_results_cognitive':
        navigate(`/hr/test-results/${candidateId}?testType=cognitive`);
        break;
      case 'view_test_results_ipip_neo':
        navigate(`/hr/test-results/${candidateId}?testType=ipip-neo`);
        break;
      case 'refresh_data':
        await fetchCandidateData();
        break;
    }
  };
  const updateCandidateStatus = async (status: string, rejectionReason?: string) => {
    try {
      const updateData: any = {
        status
      };
      if (rejectionReason) {
        updateData.rejection_reason = rejectionReason;
      }
      const {
        error
      } = await supabase.from('candidates').update(updateData).eq('id', candidateId);
      if (error) throw error;
      toast({
        title: "Επιτυχία",
        description: "Το στάδιο ενημερώθηκε επιτυχώς"
      });
      await fetchCandidateData(); // Refresh data
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  const handleInterviewScheduled = () => {
    setCurrentView('workflow');
    fetchCandidateData(); // Refresh data
  };
  const handleInterviewCompleted = async () => {
    // Update candidate status based on completed interview
    if (selectedInterview?.interview_type === 'first') {
      await updateCandidateStatus('first_interview_completed');
    } else if (selectedInterview?.interview_type === 'second') {
      await updateCandidateStatus('second_interview_completed');
    }
    
    setCurrentView('workflow');
    await fetchCandidateData(); // Refresh data to get the latest state
  };

  const handleEvaluationCompleted = async () => {
    // For evaluations, the status is already updated in InterviewForm
    setCurrentView('workflow');
    await fetchCandidateData(); // Refresh data to get the latest state
  };
  const handleTestCompleted = async () => {
    setCurrentView('workflow');
    await fetchCandidateData(); // Refresh data to get updated status
  };
  const handleDeleteInterview = async (interviewId: string) => {
    try {
      const {
        error
      } = await supabase.from('interviews').delete().eq('id', interviewId);
      if (error) throw error;
      toast({
        title: "Επιτυχία",
        description: "Η συνέντευξη διαγράφηκε επιτυχώς"
      });
      fetchCandidateData(); // Refresh data
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleInterviewUpdated = () => {
    fetchCandidateData(); // Refresh data
  };
  const handleRescheduleInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    if (interview.interview_type === 'first') {
      setCurrentView('reschedule_first');
    } else {
      setCurrentView('reschedule_second');
    }
  };

  const handleHireCandidate = async () => {
    try {
      // Συλλογή στοιχείων από υποψήφιο και συνεντεύξεις
      const secondInterview = interviews.find(i => i.interview_type === 'second' && i.status === 'completed');
      
      let guardData: any = {
        firstName: candidate.full_name.split(' ')[0] || '',
        lastName: candidate.full_name.split(' ').slice(1).join(' ') || '',
        email: candidate.email,
        mobilePhone: candidate.phone || '',
        afm: '', // Θα συμπληρωθεί χειροκίνητα
        code: '',
        hiringDate: new Date().toISOString().split('T')[0],
        dateOfBirth: '',
        licenseExpiryDate: '',
        emergencyContact: '',
        selectedTeamIds: []
      };

      // Προσθήκη στοιχείων από δεύτερη συνέντευξη αν υπάρχουν
      if (secondInterview?.interview_data) {
        const interviewData = secondInterview.interview_data;
        
        guardData = {
          ...guardData,
          firstName: interviewData.firstName || guardData.firstName,
          lastName: interviewData.lastName || guardData.lastName,
          email: interviewData.email || guardData.email,
          mobilePhone: interviewData.mobilePhone || guardData.mobilePhone,
          emergencyContact: interviewData.homePhone || guardData.emergencyContact,
          afm: interviewData.afm || interviewData.idNumber || '', // AFM ή αριθμός ταυτότητας
          dateOfBirth: interviewData.dateOfBirth || '',
          hiringDate: interviewData.hiringDate || guardData.hiringDate,
          licenseExpiryDate: interviewData.licenseExpiryDate || '',
        };
      }

      setPrepopulatedGuardData(guardData);
      setCurrentView('create_guard');
      
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleGuardCreated = async () => {
    await updateCandidateStatus('hired');
    // Ensure final_decision completed and onboarding marked completed (removed from flow)
    await supabase.from('hr_workflow_stages').upsert([
      { candidate_id: candidateId, stage_name: 'final_decision', status: 'completed', completed_at: new Date().toISOString(), notes: 'Πρόσληψη ολοκληρώθηκε' },
      { candidate_id: candidateId, stage_name: 'onboarding', status: 'completed', completed_at: new Date().toISOString(), notes: 'Onboarding ολοκληρώθηκε με πρόσληψη' }
    ]);
    setCurrentView('workflow');
    setPrepopulatedGuardData(null);
  };

  const handleDeleteCandidate = async () => {
    try {
      console.log('Starting deletion process for candidate:', candidateId);
      
      // Use the database function to safely delete candidate with all dependencies
      const { data, error } = await supabase.rpc('delete_candidate_with_dependencies', {
        candidate_id_param: candidateId
      });

      if (error) {
        console.error('Database function error:', error);
        throw error;
      }

      // Type guard for the response data
      const result = data as any;
      
      if (result && !result.success) {
        console.error('Deletion failed:', result.error);
        throw new Error(result.error || 'Unknown deletion error');
      }

      console.log('Candidate deletion completed successfully:', result);
      
      // Create description with deletion counts if available
      let description = "Ο υποψήφιος και όλα τα συνδεδεμένα δεδομένα διαγράφηκαν επιτυχώς";
      
      if (result && result.deleted_counts) {
        const counts = Object.entries(result.deleted_counts as Record<string, number>)
          .map(([table, count]) => `${table}: ${count}`)
          .join(', ');
        description += `. Διαγράφηκαν: ${counts}`;
      }
      
      toast({
        title: "Επιτυχία",
        description
      });

      // Navigate back to HR dashboard
      navigate('/hr');
      
    } catch (error: any) {
      console.error('Delete candidate error:', error);
      toast({
        title: "Σφάλμα",
        description: `Αποτυχία διαγραφής υποψηφίου: ${error.message}`,
        variant: "destructive"
      });
    }
  };
  if (loading) {
    return <div className="container mx-auto py-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">Φόρτωση...</div>
          </CardContent>
        </Card>
      </div>;
  }
  if (!candidate) {
    return <div className="container mx-auto py-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-muted-foreground">Δεν βρέθηκε υποψήφιος</p>
              <Button onClick={() => navigate('/hr')} className="mt-4">
                Επιστροφή
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  const renderCurrentView = () => {
    switch (currentView) {
      case 'schedule_first':
        return <InterviewScheduler candidateId={candidate.id} candidateName={candidate.full_name} interviewType="first" onScheduled={handleInterviewScheduled} onCancel={() => setCurrentView('workflow')} />;
      case 'schedule_second':
        return <InterviewScheduler candidateId={candidate.id} candidateName={candidate.full_name} interviewType="second" onScheduled={handleInterviewScheduled} onCancel={() => setCurrentView('workflow')} />;
      case 'conduct_first':
        return selectedInterview ? <FirstInterviewForm candidateId={candidate.id} candidateName={candidate.full_name} interviewId={selectedInterview.id} onComplete={handleInterviewCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'conduct_second':
        return selectedInterview ? <SecondInterviewForm candidateId={candidate.id} candidateName={candidate.full_name} interviewId={selectedInterview.id} onComplete={handleInterviewCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'first_test':
        return <SecurityPsychAssessment candidateId={candidate.id} candidateName={candidate.full_name} testType="cognitive" onComplete={handleTestCompleted} onCancel={() => setCurrentView('workflow')} />;
      case 'second_test':
        return <SecurityPsychAssessment candidateId={candidate.id} candidateName={candidate.full_name} testType="ipip-neo" onComplete={handleTestCompleted} onCancel={() => setCurrentView('workflow')} />;
      case 'tests': // backwards compatibility - default to cognitive test
        return <SecurityPsychAssessment candidateId={candidate.id} candidateName={candidate.full_name} testType="cognitive" onComplete={handleTestCompleted} onCancel={() => setCurrentView('workflow')} />;
      case 'test_results':
        return <TestResultsTabs candidateId={candidate.id} candidateName={candidate.full_name} onClose={() => setCurrentView('workflow')} />;
      case 'reschedule_first':
        return selectedInterview ? <InterviewRescheduler interview={selectedInterview} candidateName={candidate.full_name} onRescheduled={handleInterviewScheduled} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'reschedule_second':
        return selectedInterview ? <InterviewRescheduler interview={selectedInterview} candidateName={candidate.full_name} onRescheduled={handleInterviewScheduled} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'conduct_first_new':
        return selectedInterview ? <InterviewConductor interview={selectedInterview} candidateName={candidate.full_name} onCompleted={handleInterviewCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'conduct_second_new':
        return selectedInterview ? <InterviewConductor interview={selectedInterview} candidateName={candidate.full_name} onCompleted={handleInterviewCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'evaluate_first':
        return selectedInterview ? <InterviewForm candidateId={candidate.id} candidateName={candidate.full_name} interviewType="first" interviewId={selectedInterview.id} onCompleted={handleEvaluationCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'evaluate_second':
        return selectedInterview ? <InterviewForm candidateId={candidate.id} candidateName={candidate.full_name} interviewType="second" interviewId={selectedInterview.id} onCompleted={handleEvaluationCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'view_first_evaluation':
        return selectedInterview ? <EvaluationViewer candidateId={candidate.id} candidateName={candidate.full_name} interviewType="first" interviewId={selectedInterview.id} mode="view" onCompleted={() => setCurrentView('workflow')} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'view_second_interview':
        return selectedInterview ? <SecondInterviewForm candidateId={candidate.id} candidateName={candidate.full_name} interviewId={selectedInterview.id} onComplete={() => setCurrentView('workflow')} onCancel={() => setCurrentView('workflow')} viewMode={true} /> : null;
      case 'edit_second_interview': 
        return selectedInterview ? <SecondInterviewForm candidateId={candidate.id} candidateName={candidate.full_name} interviewId={selectedInterview.id} onComplete={handleInterviewCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'view_second_evaluation':
        return selectedInterview ? <EvaluationViewer candidateId={candidate.id} candidateName={candidate.full_name} interviewType="second" interviewId={selectedInterview.id} mode="view" onCompleted={() => setCurrentView('workflow')} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'edit_first_evaluation':
        return selectedInterview ? <EvaluationViewer candidateId={candidate.id} candidateName={candidate.full_name} interviewType="first" interviewId={selectedInterview.id} mode="edit" onCompleted={handleEvaluationCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'edit_second_evaluation':
        return selectedInterview ? <EvaluationViewer candidateId={candidate.id} candidateName={candidate.full_name} interviewType="second" interviewId={selectedInterview.id} mode="edit" onCompleted={handleEvaluationCompleted} onCancel={() => setCurrentView('workflow')} /> : null;
      case 'create_guard':
        return <AddGuardForm 
          onSuccess={handleGuardCreated} 
          onClose={() => setCurrentView('workflow')}
          prepopulatedData={prepopulatedGuardData}
        />;
      default:
        return <div className="space-y-6">
            {/* Scheduled Interviews Section */}
            {interviews.length > 0 && (
              <InterviewManager
                interviews={interviews}
                candidateName={candidate.full_name}
                onInterviewDeleted={handleDeleteInterview}
                onInterviewUpdated={handleInterviewUpdated}
              />
            )}
            
            {/* Workflow Stages */}
            <WorkflowStages candidateId={candidate.id} candidateName={candidate.full_name} currentStage={candidate.status} interviews={interviews} onStageAction={handleStageAction} />
          </div>;
    }
  };
  return <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/hr')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Επιστροφή
              </Button>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {candidate.full_name}
                </CardTitle>
                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                  <span>📧 {candidate.email}</span>
                  <span>📞 {candidate.phone}</span>
                  <span>📅 Αίτηση: {new Date(candidate.application_date).toLocaleDateString('el-GR')}</span>
                </div>
              </div>
            </div>
            
            {currentView === 'workflow' && <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentView('test_results')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Αποτελέσματα Τεστ
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Διαγραφή
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Επιβεβαίωση Διαγραφής</AlertDialogTitle>
                      <AlertDialogDescription>
                        Είστε σίγουροι ότι θέλετε να διαγράψετε τον υποψήφιο <strong>{candidate.full_name}</strong>;
                        <br /><br />
                        Αυτή η ενέργεια θα διαγράψει:
                        <ul className="list-disc ml-5 mt-2">
                          <li>Όλες τις συνεντεύξεις</li>
                          <li>Όλα τα αποτελέσματα τεστ</li>
                          <li>Όλα τα στάδια workflow</li>
                          <li>Όλες τις αξιολογήσεις</li>
                          <li>Τον υποψήφιο οριστικά</li>
                        </ul>
                        <br />
                        <strong>Αυτή η ενέργεια δεν μπορεί να αναιρεθεί!</strong>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDeleteCandidate}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Διαγραφή Οριστικά
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>}
          </div>
        </CardHeader>
      </Card>

      {/* Main Content */}
      {renderCurrentView()}
    </div>;
}